import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, Clock, Pencil } from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const emptyForm = {
  name: "",
  discountPercentage: "",
  days: [1, 2, 3, 4, 5],
  startTime: "15:00",
  endTime: "18:00",
};

export default function HappyHourSettings({ onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchList = async () => {
    try {
      const res = await api.get("/happy-hours");
      setList(res.data.data || []);
    } catch {
      toast.error("Failed to load happy hours");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const toggleDay = (d) => {
    setForm((prev) => ({
      ...prev,
      days: prev.days.includes(d) ? prev.days.filter((x) => x !== d) : [...prev.days, d],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    const pct = parseFloat(form.discountPercentage);
    if (!pct || pct < 1 || pct > 100) return toast.error("Discount must be 1–100%");
    if (!form.days.length) return toast.error("Select at least one day");
    if (form.startTime >= form.endTime) return toast.error("End time must be after start time");

    setSaving(true);
    try {
      const payload = { ...form, discountPercentage: pct };
      if (editingId) {
        const res = await api.put(`/happy-hours/${editingId}`, payload);
        setList((prev) => prev.map((h) => (h._id === editingId ? res.data.data : h)));
        toast.success("Updated");
      } else {
        const res = await api.post("/happy-hours", payload);
        setList((prev) => [res.data.data, ...prev]);
        toast.success("Happy hour created");
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (hh) => {
    try {
      const res = await api.put(`/happy-hours/${hh._id}`, { isActive: !hh.isActive });
      setList((prev) => prev.map((h) => (h._id === hh._id ? res.data.data : h)));
    } catch {
      toast.error("Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this happy hour?")) return;
    try {
      await api.delete(`/happy-hours/${id}`);
      setList((prev) => prev.filter((h) => h._id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const startEdit = (hh) => {
    setForm({
      name: hh.name,
      discountPercentage: hh.discountPercentage,
      days: hh.days,
      startTime: hh.startTime,
      endTime: hh.endTime,
    });
    setEditingId(hh._id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/25 flex items-center justify-center">
              <Clock size={15} className="text-amber-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Happy Hours</h2>
              <p className="text-[10px] text-slate-400">Auto-apply discounts on schedule</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Existing list */}
          {loading ? (
            <p className="text-xs text-slate-400 text-center py-6">Loading...</p>
          ) : list.length === 0 && !showForm ? (
            <div className="text-center py-8">
              <Clock size={28} className="text-slate-200 mx-auto mb-2" />
              <p className="text-xs text-slate-400">No happy hours configured yet</p>
            </div>
          ) : (
            list.map((hh) => (
              <div
                key={hh._id}
                className={`border rounded-xl px-4 py-3 flex items-start justify-between gap-3 transition-colors ${
                  hh.isActive ? "border-amber-200 bg-amber-50/50" : "border-slate-200 bg-slate-50/50 opacity-60"
                }`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-slate-700 truncate">{hh.name}</span>
                    <span className="shrink-0 text-[10px] font-black text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-md">
                      -{hh.discountPercentage}%
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500">
                    {hh.startTime} – {hh.endTime} &nbsp;·&nbsp;{" "}
                    {hh.days.sort().map((d) => DAY_LABELS[d]).join(", ")}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {/* Toggle */}
                  <button
                    onClick={() => handleToggleActive(hh)}
                    title={hh.isActive ? "Disable" : "Enable"}
                    className={`w-8 h-4.5 rounded-full relative transition-colors ${
                      hh.isActive ? "bg-amber-400" : "bg-slate-300"
                    }`}
                    style={{ minWidth: 32, height: 18 }}
                  >
                    <span
                      className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full shadow transition-all ${
                        hh.isActive ? "left-[14px]" : "left-0.5"
                      }`}
                    />
                  </button>
                  <button
                    onClick={() => startEdit(hh)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <Pencil size={12} />
                  </button>
                  <button
                    onClick={() => handleDelete(hh._id)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))
          )}

          {/* Form */}
          {showForm && (
            <div className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50">
              <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                {editingId ? "Edit" : "New"} Happy Hour
              </p>

              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Name</label>
                <input
                  type="text"
                  placeholder="e.g. Evening Happy Hour"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 bg-white"
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">
                  Discount (%)
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  placeholder="e.g. 15"
                  value={form.discountPercentage}
                  onChange={(e) => setForm((p) => ({ ...p, discountPercentage: e.target.value }))}
                  className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 bg-white"
                />
              </div>

              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">Start</label>
                  <input
                    type="time"
                    value={form.startTime}
                    onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 bg-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1">End</label>
                  <input
                    type="time"
                    value={form.endTime}
                    onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
                    className="w-full text-xs border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-amber-400 bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Days</label>
                <div className="flex gap-1.5 flex-wrap">
                  {DAY_LABELS.map((label, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => toggleDay(i)}
                      className={`text-[10px] font-bold px-2.5 py-1 rounded-md border transition-colors ${
                        form.days.includes(i)
                          ? "bg-amber-500 border-amber-500 text-white"
                          : "bg-white border-slate-200 text-slate-500 hover:border-amber-300"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-1">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 text-xs font-bold bg-amber-500 hover:bg-amber-600 text-white rounded-lg py-2 transition-colors disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingId ? "Update" : "Create"}
                </button>
                <button
                  onClick={cancelForm}
                  className="flex-1 text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg py-2 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!showForm && (
          <div className="px-5 py-3 border-t border-slate-100">
            <button
              onClick={() => setShowForm(true)}
              className="w-full flex items-center justify-center gap-2 text-xs font-bold text-amber-600 bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl py-2.5 transition-colors"
            >
              <Plus size={13} />
              Add Happy Hour
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
